import SelectDamagedPartGroup from 'components/elements/form/DamagedPartGroup/SelectDamagedPartGroup'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import RequestTemplate from 'components/templates/RequestTemplate'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import React from 'react'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import {
  FlatTireCauseTypes,
  AccidentPartInfo,
  SpareTireStatusTypes,
  DestinationTypes,
  QnStatesProps,
  initialQnEditState,
} from 'states/QnState'
import {
  StatesProps,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import { isSjDomain } from 'utils/domain'
import SwitchButtonGroup from 'components/elements/form/SwitchButtonGroup'
import { OptionItem } from 'components/elements/common'
import { success } from 'utils/status'

const spareTireStatusOptions: Array<OptionItem<
  SpareTireStatusTypes
>> = Object.entries(VEHICLE_STATE_FORM.spareTireStatus.options).map(
  ([k, v]) =>
    ({ label: v as SpareTireStatusTypes, value: k } as OptionItem<
      SpareTireStatusTypes
    >)
)

const flatTireCauseOptions: Array<OptionItem<
  FlatTireCauseTypes
>> = Object.entries(VEHICLE_STATE_FORM.flatTireCause.options).map(
  ([k, v]) =>
    ({ label: v as FlatTireCauseTypes, value: k } as OptionItem<
      FlatTireCauseTypes
    >)
)

/**
 * QN-03-c: パンク
 */
const FlatTire: React.FC<StatesProps> = (props: QnStatesProps) => {
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const { state, setState } = props
  const {
    destinationType,
    isNotApplicableParts,
    spareTireStatus,
    accidentPart,
    isMotorbike,
    flatTireCause,
  } = editState.vehicleStateInfo

  // バリデーション
  const isDisabled = React.useMemo(() => {
    if (accidentPart.parts.length <= 0) {
      return !isNotApplicableParts
    }
    if (
      !spareTireStatus.includes(SpareTireStatusTypes.yes) &&
      destinationType.includes(DestinationTypes.specify)
    ) {
      return !editState.destinationLocation.isSelected
    }
    return false
  }, [editState.vehicleStateInfo, editState.destinationLocation])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    if (spareTireStatus.includes(SpareTireStatusTypes.yes)) {
      return ''
    }
    switch (destinationType) {
      case DestinationTypes.specify:
        return WARNING.WRECKER
      case DestinationTypes.introduction:
        return WARNING.INTRODUCTION
    }
  }, [destinationType, spareTireStatus])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo((): string => {
    return props.state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : PAGE_PATH.TROUBLE_PLACE
  }, [])

  /** 破損箇所情報変更時のコールバック */
  const handleDamagedPartChanged = React.useCallback(
    (value: AccidentPartInfo): void => {
      setVehicleStateInfo({
        accidentPart: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** バイクでのパンクトラブルスイッチ変更時のコールバック */
  const handleIsMotorbikeChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        isMotorbike: value,
        accidentPart: {
          ...initialQnEditState.vehicleStateInfo.accidentPart,
        },
      })
    },
    [setVehicleStateInfo]
  )

  /** タイヤ軸が複数ある車両トラブルのスイッチ変更時のコールバック */
  const handleIsNotApplicablePartsChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        isNotApplicableParts: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** スペアタイヤの状態選択変更時のコールバック */
  const handleSpareTireStatusChanged = React.useCallback(
    (value: SpareTireStatusTypes) => {
      setVehicleStateInfo({
        spareTireStatus: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** パンク原因の選択変更時のコールバック */
  const handleFlatTireCauseChanged = React.useCallback(
    (value: FlatTireCauseTypes) => {
      setVehicleStateInfo({
        flatTireCause: value,
      })
    },
    [setVehicleStateInfo]
  )

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: editState.vehicleStateInfo,
      destinationLocation: editState.destinationLocation,
    })
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    if (state.confirmationChangeType) {
      setEditingItem({
        ...editState,
        vehicleStateInfo: state.vehicleStateInfo,
        troubleDatetime: state.troubleDatetime,
        vehicleState: state.vehicleState,
        destinationLocation: state.destinationLocation,
      })
    } else {
      setState({
        ...state,
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      })
      setEditingItem({
        ...editState,
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      })
    }
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.destinationLocation,
  ])

  return (
    <RequestTemplate
      title={'パンク'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          <SelectDamagedPartGroup
            title={`${VEHICLE_STATE_FORM.flatTirePart.formName}を選択（複数選択可）`}
            isRequired={!isNotApplicableParts}
            damagedStatus={{
              ...accidentPart,
            }}
            isMotorbike={isMotorbike}
            onChanged={(value: AccidentPartInfo): void => {
              handleDamagedPartChanged(value)
            }}
          />
          {isSjDomain() && (
            <>
              <SwitchButtonGroup
                title={`${VEHICLE_STATE_FORM.motorbikeFlatTire.formName}`}
                isSwitched={isMotorbike}
                label={`${VEHICLE_STATE_FORM.motorbikeFlatTire.label}`}
                onChanged={handleIsMotorbikeChanged}
              />
              <SwitchButtonGroup
                title={`${VEHICLE_STATE_FORM.notApplicableParts.formName}`}
                isSwitched={isNotApplicableParts}
                label={`${VEHICLE_STATE_FORM.notApplicableParts.label}`}
                onChanged={handleIsNotApplicablePartsChanged}
              />
            </>
          )}
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.spareTireStatus.formName}を選択`}
            isRequired
            options={spareTireStatusOptions}
            selectedValue={spareTireStatus}
            onValueChanged={handleSpareTireStatusChanged}
          />
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.flatTireCause.formName}を選択`}
            isRequired
            options={flatTireCauseOptions}
            selectedValue={flatTireCause}
            onValueChanged={handleFlatTireCauseChanged}
          />
          <SelectVehicleModificationContent />
          {!spareTireStatus.includes(SpareTireStatusTypes.yes) && (
            <SelectDestinationContent />
          )}
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      isDisabled={isDisabled}
      notClearEditingItem
    />
  )
}

export default FlatTire
